import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UserRole } from '@app/core/auth/user-role.enum';
import { UserClaims } from '@app/core/services/auth-service/auth-service.model';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oauthService = inject(OAuthService);
  private router = inject(Router);

  private userClaimsSignal = signal<UserClaims | null>(null);

  username = computed(() => this.userClaimsSignal()?.email || '');
  userRoles = computed(() => this.userClaimsSignal()?.roles || []);
  tenantId = computed(() => this.userClaimsSignal()?.tenant_id || null);

  constructor() {
    this.oauthService.setupAutomaticSilentRefresh();

    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      this.cleanUpUrl();
    });

    this.oauthService.events.subscribe(() => {
      this.updateUserClaims();
    });
    this.oauthService.events.pipe(filter(e => e.type === 'token_received')).subscribe(() => this.updateUserClaims());
    if (this.oauthService.hasValidAccessToken()) {
      this.updateUserClaims();
    }
  }

  logout() {
    this.oauthService.revokeTokenAndLogout();
  }

  isInRole(role: UserRole): boolean {
    return this.userRoles().includes(role);
  }

  getTenantId(): number | null {
    const roles = this.userClaimsSignal()?.roles || [];
    if (roles.includes(UserRole.SUPER_ADMIN)) {
      return null;
    }
    return this.tenantId();
  }

  private updateUserClaims() {
    const claims = this.oauthService.getIdentityClaims() as UserClaims | null;

    if (!claims) {
      this.userClaimsSignal.set(null);
      return;
    }
    const userClaims: UserClaims = {
      email: claims.email ?? '',
      roles: claims.roles as UserRole[],
      tenant_id: claims.tenant_id,
    };

    this.userClaimsSignal.set(userClaims);
  }

  private cleanUpUrl() {
    const urlTree = this.router.parseUrl(this.router.url);

    const keycloakParams = ['state', 'session_state', 'iss', 'code', 'error', 'error_description'];

    const hasKeycloakParams = Object.keys(urlTree.queryParams).some(key => keycloakParams.includes(key));

    if (hasKeycloakParams) {
      const filteredParams = Object.keys(urlTree.queryParams)
        .filter(key => !keycloakParams.includes(key))
        .reduce<Record<string, string | string[]>>((obj, key) => {
          obj[key] = urlTree.queryParams[key] as string | string[];
          return obj;
        }, {});

      void this.router.navigate([], {
        queryParams: filteredParams,
        replaceUrl: true,
      });
    }
  }
}
